import { useState, useEffect, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Image, Trash2, Loader2, Search, Monitor, Smartphone, AlertTriangle } from "lucide-react";

// Creative format dimensions - FIXED
const FORMATS = {
  BANNER: { 
    width: 320, 
    height: 100, 
    label: "Banner", 
    ratio: "320×100", 
    icon: Smartphone,
    description: "Displayed in the Booma passenger app during active rides."
  },
  PANEL: { 
    width: 1920, 
    height: 1080, 
    label: "Panel", 
    ratio: "1920×1080 (16:9)", 
    icon: Monitor,
    description: "Shown on the in-car tablet display mounted in the vehicle."
  }
};

export default function AdvertiserCreatives() {
  const { advertiser, advertiserId } = useOutletContext();
  const { token } = useAuth();
  const [creatives, setCreatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [creativeToDelete, setCreativeToDelete] = useState(null);

  useEffect(() => {
    fetchCreatives();
  }, [advertiserId]);

  const fetchCreatives = async () => {
    try {
      const response = await axios.get(`${API}/accounts/${advertiserId}/creatives`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreatives(response.data);
    } catch (error) {
      toast.error("Failed to fetch creatives");
    } finally {
      setLoading(false);
    }
  };

  const filteredCreatives = useMemo(() => {
    if (!searchQuery) return creatives;
    const query = searchQuery.toLowerCase();
    return creatives.filter(c => c.name?.toLowerCase().includes(query));
  }, [creatives, searchQuery]);

  const bannerCreatives = filteredCreatives.filter(c => c.format === 'BANNER');
  const panelCreatives = filteredCreatives.filter(c => c.format === 'PANEL');

  const handleDelete = async () => {
    if (!creativeToDelete) return;
    try {
      await axios.delete(`${API}/creatives/${creativeToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Creative deleted");
      setDeleteDialogOpen(false);
      setCreativeToDelete(null);
      fetchCreatives();
    } catch (error) {
      toast.error("Failed to delete creative");
    }
  };

  // Creative card component
  const CreativeCard = ({ creative, format }) => (
    <Card 
      key={creative.id} 
      className="bg-card border-border overflow-hidden group hover:shadow-lg transition-all"
    >
      <div 
        className="relative bg-secondary/50"
        style={{ aspectRatio: format === 'BANNER' ? '320/100' : '16/9' }}
      >
        <img 
          src={creative.file_url} 
          alt={creative.name}
          className="w-full h-full object-cover"
          onError={(e) => { e.target.style.background = '#27272A'; e.target.alt = 'No Preview'; }}
        />
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          <Button 
            variant="destructive" 
            size="sm"
            onClick={() => { setCreativeToDelete(creative); setDeleteDialogOpen(true); }}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Delete
          </Button>
        </div>
        <Badge 
          className={`absolute top-2 right-2 text-xs ${
            creative.source === 'AI_GENERATED' 
              ? 'bg-purple-500/80' 
              : creative.source === 'URL_IMPORTED'
                ? 'bg-green-500/80'
                : 'bg-blue-500/80'
          }`}
        >
          {creative.source === 'AI_GENERATED' ? 'AI' : creative.source === 'URL_IMPORTED' ? 'URL' : 'Uploaded'}
        </Badge>
      </div>
      <CardContent className="p-3">
        <h4 className="font-medium truncate text-sm">{creative.name}</h4>
        <p className="text-xs text-muted-foreground mt-1">
          {creative.width}×{creative.height} • {new Date(creative.created_at).toLocaleDateString()}
        </p>
      </CardContent>
    </Card>
  );

  // Format column component
  const FormatColumn = ({ format, creativesList }) => {
    const formatInfo = FORMATS[format];
    const FormatIcon = formatInfo.icon;
    
    return (
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-primary/10">
            <FormatIcon className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="font-semibold">{formatInfo.label}s</h2>
            <p className="text-xs text-muted-foreground">
              Required: <span className="font-mono text-primary">{formatInfo.ratio}</span>
            </p>
          </div>
          <Badge variant="outline" className="ml-auto text-xs">
            {creativesList.length}
          </Badge>
        </div>

        <p className="text-sm text-muted-foreground mb-4">
          {formatInfo.description}
        </p>

        {creativesList.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="p-6 text-center">
              <Image className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                No {formatInfo.label.toLowerCase()}s
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {creativesList.map(creative => (
              <CreativeCard key={creative.id} creative={creative} format={format} />
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search Bar */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input 
          placeholder="Search creatives..." 
          value={searchQuery} 
          onChange={(e) => setSearchQuery(e.target.value)} 
          className="pl-10" 
        />
      </div>

      {/* Info Banner */}
      <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-blue-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-600">Admin View</p>
            <p className="text-sm text-muted-foreground">
              You are viewing creatives for <span className="font-semibold">{advertiser?.company_name || advertiser?.name || 'this advertiser'}</span>. 
              Advertisers manage their own creatives through their dashboard.
            </p>
          </div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <FormatColumn format="BANNER" creativesList={bannerCreatives} />
        <FormatColumn format="PANEL" creativesList={panelCreatives} />
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Creative?</DialogTitle>
            <DialogDescription>
              This will permanently delete &ldquo;{creativeToDelete?.name}&rdquo;. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
